import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { REPORT_CONSTANTS } from './Reports/ReportConstants';
import CompactReportsFilter from './Filters/CompactReportsFilter';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';


// Report Components
import PartywiseSalesReport from './Reports/PartywiseSalesReport';
import DetailedSalesReport from './Reports/DetailedSalesReport';
import CustomerLedgerReport from './Reports/CustomerLedgerReport';
import InvoiceCollectionReport from './Reports/InvoiceCollectionReport';
import PaymentRegisterReport from './Reports/PaymentRegisterReport';
import AgingReport from './Reports/AgingReport';
import ItemwiseSalesReport from './Reports/ItemwiseSalesReport';
import PurchaseBillsSummary from './Reports/PurchaseBillsReport';
import StockReport from './Reports/StockReport';
// GST reports moved under Taxes section
import ProfitLossReport from './Reports/ProfitLossReport';
import BalanceSheetReport from './Reports/BalanceSheetReport';
import CashFlowReport from './Reports/CashFlowReport';
import EmployeeCashFlowReport from './Reports/EmployeeCashFlowReport';

// Helper to get current financial year start and end
function getCurrentFinancialYearRange() {
  const now = new Date();
  const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  const start = new Date(year, 3, 1); // April 1
  const end = new Date(year + 1, 2, 31, 23, 59, 59, 999); // March 31
  return { start, end };
}

// Helper to get current financial year (just the year number)
function getCurrentFinancialYear() {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
}

// Helper function to get specific financial year range
function getFinancialYearRange(financialYear) {
  const start = new Date(financialYear, 3, 1); // April 1
  const end = new Date(financialYear + 1, 2, 31, 23, 59, 59, 999); // March 31
  return { start, end };
}

// Helper to get a broader date range for testing (last 2 years)
function getBroaderDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear() - 2, 0, 1); // 2 years ago
  const end = new Date(now.getFullYear() + 1, 11, 31, 23, 59, 59, 999); // Next year end
  return { start, end };
}

const Reports = ({ db, userId, isAuthReady, appId }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();

  const ALL_REPORT_IDS = useMemo(() => REPORT_CONSTANTS.REPORT_TYPES.map(r => r.value), []);

  const initialReport = ALL_REPORT_IDS.includes(reportId || '') ? reportId : 'partywise-sales';
  const [selectedReport, setSelectedReport] = useState(initialReport);
  const [dateRange, setDateRange] = useState(() => getCurrentFinancialYearRange());
  const [financialYear, setFinancialYear] = useState(() => getCurrentFinancialYear());
  const [selectedParty, setSelectedParty] = useState('');
  const [parties, setParties] = useState([]);
  const [partiesLoaded, setPartiesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [hydrated, setHydrated] = useState(false); // URL -> state hydration complete
  const [txnFilter, setTxnFilter] = useState('all'); // Transaction mode filter

  // Sync date range when financial year changes
  useEffect(() => {
    if (!financialYear) return;
    const start = new Date(financialYear, 3, 1); // April 1 of selected FY
    const end = new Date(financialYear + 1, 2, 31, 23, 59, 59, 999); // March 31 of next year
    console.log('📊 FINANCIAL YEAR FILTER CHANGED:', {
      selectedYear: financialYear,
      financialYearRange: `${financialYear}-${financialYear + 1}`,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
    setDateRange({ start, end });
  }, [financialYear]);

  // Read reportId from URL and keep state in sync
  useEffect(() => {
    if (!reportId) return; // handled by default route
    if (ALL_REPORT_IDS.includes(reportId) && reportId !== selectedReport) {
      setSelectedReport(reportId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Parse query params -> state
  useEffect(() => {
    const startQ = searchParams.get('start');
    const endQ = searchParams.get('end');
    const fyQ = searchParams.get('fy');
    const partyQ = searchParams.get('party') || '';

    let nextDateRange = null;
    if (startQ && endQ) {
      const s = new Date(startQ);
      const e = new Date(endQ);
      if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
        nextDateRange = { start: s, end: e };
      }
    }
    if (nextDateRange) {
      const curS = dateRange.start.toISOString().slice(0,10);
      const curE = dateRange.end.toISOString().slice(0,10);
      if (curS !== searchParams.get('start') || curE !== searchParams.get('end')) {
        setDateRange(nextDateRange);
      }
    }
    if (fyQ && Number(fyQ) !== financialYear) {
      const num = parseInt(fyQ, 10);
      if (!isNaN(num)) setFinancialYear(num);
    }
    if (partyQ !== selectedParty) setSelectedParty(partyQ);
    if (!hydrated) setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // State -> URL (keep query params and route in sync)
  useEffect(() => {
    if (!hydrated) return; // wait until URL params have been read once
    const desiredPath = `/reports/${selectedReport}`;
    // Update path if needed
    if (reportId !== selectedReport) {
      navigate(desiredPath, { replace: true });
    }
    // Update query params
    const qp = new URLSearchParams(searchParams);
    qp.set('start', dateRange.start.toISOString().split('T')[0]);
    qp.set('end', dateRange.end.toISOString().split('T')[0]);
    qp.set('fy', String(financialYear));
    if (selectedParty) qp.set('party', selectedParty); else qp.delete('party');
    const current = searchParams.toString();
    const next = qp.toString();
    if (current !== next) setSearchParams(qp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, selectedReport, dateRange.start, dateRange.end, financialYear, selectedParty]);

  // Fetch parties for filters
  useEffect(() => {
    if (!isAuthReady || !userId || !appId || !db || db.type === 'mock-firestore' || !companyId) {
      setParties([]);
      setPartiesLoaded(true);
      return;
    }

    try {
      const partiesQuery = query(
        getCollectionForCompany(db, { companyId, appId, userId }, 'parties'),
        orderBy('firmName', 'asc')
      );
      const unsubscribe = onSnapshot(partiesQuery, (snapshot) => {
        const partiesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setParties(partiesData);
        setPartiesLoaded(true);
      }, (err) => {
        console.error('Error fetching parties:', err);
        setPartiesLoaded(true);
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error fetching parties:', error);
      setPartiesLoaded(true);
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch company details
  useEffect(() => {
    if (!isAuthReady || !db || db.type === 'mock-firestore' || !companyId) return;

    const companyDocRef = doc(db, 'companies', companyId);
    const unsubscribe = onSnapshot(companyDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setCompanyDetails(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
      }
    });
    return () => unsubscribe();
  }, [db, isAuthReady, companyId]);


  // Filter report types based on company GST type
  const reportTypes = useMemo(() => {
    const all = REPORT_CONSTANTS.REPORT_TYPES;
    const type = (companyDetails?.gstinType || '').toLowerCase();
    return all.filter(rt => {
      if (rt.value === 'gst-summary-regular') return type === 'regular';
      if (rt.value === 'gst-summary-composition') return type === 'composition';
      if (rt.value === 'gst-hsn-summary-regular') return type === 'regular';
      if (rt.value === 'gst-hsn-summary-composition') return type === 'composition';
      return true;
    });
  }, [companyDetails]);



  // Memoize common props to prevent infinite re-renders
  const commonProps = useMemo(() => ({
    db,
    userId,
    appId,
    companyId,
    dateRange,
    financialYear,
    selectedParty,
    parties,
    loading,
    setLoading,
    setSelectedParty,
    isAuthReady,
    companyDetails,
    txnFilter,
    setTxnFilter
  }), [db, userId, appId, companyId, dateRange, financialYear, selectedParty, parties, loading, isAuthReady, companyDetails, txnFilter, setSelectedParty]);

  const renderReportComponent = () => {
    switch (selectedReport) {
      case 'partywise-sales':
        return <PartywiseSalesReport {...commonProps} />;
      case 'detailed-sales':
        return <DetailedSalesReport {...commonProps} />;
      case 'customer-ledger':
        return <CustomerLedgerReport {...commonProps} />;
      case 'invoice-collection':
        return <InvoiceCollectionReport {...commonProps} />;
      case 'payment-register':
        return <PaymentRegisterReport {...commonProps} />;
      case 'aging-report':
        return <AgingReport {...commonProps} />;
      case 'itemwise-sales':
        return <ItemwiseSalesReport {...commonProps} />;
      case 'purchase-bills-summary':
        return <PurchaseBillsSummary {...commonProps} />;
      case 'stock-report':
        return <StockReport {...commonProps} />;
      case 'profit-loss':
        return <ProfitLossReport {...commonProps} />;
      case 'balance-sheet':
        return <BalanceSheetReport {...commonProps} />;
      case 'trial-balance':
        return null; // Trial Balance removed
      case 'cash-flow':
        return <CashFlowReport {...commonProps} />;
      case 'employee-cash-flow':
        return <EmployeeCashFlowReport {...commonProps} />;
      default:
        return <PartywiseSalesReport {...commonProps} />;
    }
  };

  if (!isAuthReady) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center text-gray-600">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 report-page-root max-w-full">
      <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 max-w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-0">{t('reportsDashboard')}</h1>
        </div>

        {/* Report Type Selector and Filters */}
        <div className="space-y-4 mb-6">
          {/* Report Type Selector */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('selectReportType')}
            </label>
            <AppSelect
              id="report-type-select"
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              aria-label={t('selectReportType')}
              sheetTitle={t('selectReportType')}
              options={reportTypes.map((report) => ({
                value: report.value,
                label: `${report.icon} ${report.label}`,
              }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg px-4 py-3 text-sm sm:text-base shadow-sm`}
            />
          </div>

          {/* Compact Filters */}
          <CompactReportsFilter
            parties={parties}
            dateRange={dateRange}
            setDateRange={setDateRange}
            financialYear={financialYear}
            setFinancialYear={setFinancialYear}
            selectedParty={selectedParty}
            setSelectedParty={setSelectedParty}
            txnFilter={txnFilter}
            setTxnFilter={setTxnFilter}
            showTransactionFilter={selectedReport === 'cash-flow'}
            requirePartySelection={selectedReport === 'detailed-sales'}
            onFiltersChange={() => {
              // Trigger any necessary updates when filters change
            }}
          />
        </div>

        {/* Report Content */}
        <div id="reports-list" className="bg-white border border-gray-200 rounded-lg max-w-full overflow-hidden">
          {selectedReport === 'partywise-sales' && !partiesLoaded ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('loadingParties')}</p>
            </div>
          ) : (
            renderReportComponent()
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports; 